import { Component } from '@theme/component';

/**
 * Homepage peak directory: filters the server-rendered peak rows as the visitor types.
 * The full list works without JavaScript; the search field only appears once this runs.
 *
 * @typedef {object} Refs
 * @property {HTMLLabelElement} search - Search field wrapper (hidden until enhanced).
 * @property {HTMLInputElement} input - Search input.
 * @property {HTMLElement} status - Live region announcing the match count.
 * @property {HTMLElement} results - Scrolling results region.
 * @property {HTMLUListElement} list - Peak rows.
 * @property {HTMLElement} empty - Empty-state message.
 *
 * @extends {Component<Refs>}
 */
class PeakDirectoryComponent extends Component {
  requiredRefs = ['search', 'input', 'status', 'results', 'list', 'empty'];

  /** @type {number | null} */
  #announceTimer = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.search.hidden = false;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#announceTimer !== null) clearTimeout(this.#announceTimer);
  }

  filter() {
    const query = this.refs.input.value.trim().toLowerCase();
    let matches = 0;

    for (const row of this.refs.list.querySelectorAll('[data-search]')) {
      if (!(row instanceof HTMLElement)) continue;
      const isMatch = !query || (row.dataset.search ?? '').includes(query);
      row.hidden = !isMatch;
      if (isMatch) matches += 1;
    }

    this.refs.empty.hidden = matches > 0;
    this.refs.results.scrollTop = 0;

    // Announce once typing pauses so screen readers aren't flooded.
    if (this.#announceTimer !== null) clearTimeout(this.#announceTimer);
    this.#announceTimer = window.setTimeout(() => {
      const { template = '', templateOne = '' } = this.refs.status.dataset;
      const message = matches === 1 ? templateOne : template.replace('[count]', String(matches));
      this.refs.status.textContent = query ? message : '';
    }, 400);
  }
}

if (!customElements.get('peak-directory-component')) {
  customElements.define('peak-directory-component', PeakDirectoryComponent);
}
